/**
 * Claims Filing Review Utilities
 *
 * Small pure helpers used by the claims review step for building human
 * readable displays and computing default search terms.
 */
import { CoverageSchema } from '@/services/types/coverage-types';

import { ReviewedFieldState } from '../hooks/useClaimFilingState';

/**
 * Build a compact single-line vehicle label from the first vehicle on a
 * coverage, e.g. "2020 Toyota Camry (Plate ABC123)". Returns an empty
 * string when no vehicle is present.
 */
export const buildVehicleDisplay = (
  coverage: CoverageSchema | undefined,
): string => {
  const v = coverage?.vehicles?.[0];
  if (!v) return '';
  const base = [v.year, v.make, v.model].filter(Boolean).join(' ');
  return v.license_plate ? `${base} (Plate ${v.license_plate})` : base;
};

/**
 * Compute a best-effort initial search term for transcript/evidence lookup
 * based on the current active field, falling back to vehicle info and then
 * to common policy identifiers.
 */
export const computeInitialSearchTerm = (
  reviewedFields: ReviewedFieldState[],
  activeFieldKey: string | null,
  coverage: CoverageSchema | undefined,
): string => {
  const clientActiveVal = (reviewedFields.find((f) => f.key === activeFieldKey)
    ?.value ?? '') as string;
  if (clientActiveVal && clientActiveVal.trim()) return clientActiveVal.trim();

  // When hovering specific rows in Vehicle/Insurance sections, derive the
  // search term from that precise field rather than a concatenated label.
  if (activeFieldKey) {
    // Vehicle fields: vehicle:<index>:(year|make|model|plate)
    const vehMatch = activeFieldKey.match(
      /^vehicle:(\d+):(year|make|model|plate)$/,
    );
    if (vehMatch) {
      const index = Number(vehMatch[1]);
      const field = vehMatch[2] as 'year' | 'make' | 'model' | 'plate';
      const v = coverage?.vehicles?.[index];
      if (v) {
        const value = (() => {
          switch (field) {
            case 'year':
              return (v as { year?: string | number }).year ?? '';
            case 'make':
              return (v as { make?: string }).make ?? '';
            case 'model':
              return (v as { model?: string }).model ?? '';
            case 'plate':
              return (v as { license_plate?: string }).license_plate ?? '';
            default:
              return '';
          }
        })();
        const str = String(value || '').trim();
        if (str) return str;
      }
    }

    // Insurance fields: ins:(carrier|insured|policy)
    const insMatch = activeFieldKey.match(/^ins:(carrier|insured|policy)$/);
    if (insMatch) {
      const field = insMatch[1] as 'carrier' | 'insured' | 'policy';
      const value = (() => {
        switch (field) {
          case 'carrier':
            return coverage?.carrier?.name ?? '';
          case 'insured': {
            const list = coverage?.named_insured || [];
            return list[0] || list.join(', ') || '';
          }
          case 'policy': {
            const identifiers = coverage?.policy_identifiers || [];
            // Prefer explicit POLICY_NUMBER; fall back to the first identifier
            const primary = identifiers.find(
              (p) =>
                (p as { identifier_type?: string }).identifier_type ===
                'policy_number',
            );
            return (
              (primary as { identifier_value?: string } | undefined)
                ?.identifier_value ||
              (identifiers[0] as { identifier_value?: string } | undefined)
                ?.identifier_value ||
              ''
            );
          }
          default:
            return '';
        }
      })();
      const str = String(value || '').trim();
      if (str) return str;
    }
  }

  const vehicleVal = buildVehicleDisplay(coverage);
  if (vehicleVal && vehicleVal.trim()) return vehicleVal.trim();

  const carrier = coverage?.carrier?.name || '';
  const policy = coverage?.policy_identifiers?.[0]?.identifier_value || '';
  const insured = coverage?.named_insured?.[0] || '';
  return (carrier || policy || insured || '').trim();
};
